import React, { useState } from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import { DashboardBusiness, useDashboardEmployees } from '~/hooks/apis/business-traveller/useBusinessDashboard';

import BusinessDashboardEmployeeRow from './BusinessDashboardEmployeeRow';

export default function BusinessDashboardEmployees({ business }: { business: DashboardBusiness }) {
  const [page, setPage] = useState(0);
  const { data, isLoading, isError } = useDashboardEmployees(business.id, true);
  if (isLoading) {
    return <CircularProgress size={24} />;
  }
  if (isError) {
    return (
      <Alert severity="error">Unable to load employee names from production. Try expanding the business again.</Alert>
    );
  }
  const liveEmployees = new Map(data?.map((employee) => [employee.id, employee]));
  const snapshotEmployees = new Map(business.employees.map((employee) => [employee.id, employee]));
  const ids = Array.from(new Set([...liveEmployees.keys(), ...snapshotEmployees.keys()])).sort((a, b) => {
    const ttvA = snapshotEmployees.get(a)?.ttv ?? -Infinity;
    const ttvB = snapshotEmployees.get(b)?.ttv ?? -Infinity;
    return ttvA === ttvB ? 0 : ttvB - ttvA;
  });
  return (
    <Box p={2} bgcolor="background.default">
      <Typography variant="h6" mb={1}>
        Employees · {business.name}
      </Typography>
      <Table size="small" aria-label={`${business.name} employees`}>
        <TableHead>
          <TableRow>
            {[
              'Employee',
              'TTV (AUD)',
              'Margin (AUD)',
              'Orders',
              'Société status',
              'Silver granted by LEBT',
              'Silver grant date / progress',
            ].map((label) => (
              <TableCell key={label}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ids.slice(page * 10, page * 10 + 10).map((id) => (
            <BusinessDashboardEmployeeRow
              key={id}
              businessId={business.id}
              employee={liveEmployees.get(id)}
              metrics={snapshotEmployees.get(id)}
            />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={ids.length}
        page={page}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        onPageChange={(_, next) => setPage(next)}
      />
      {!ids.length && <Typography p={2}>No employees registered.</Typography>}
      <Typography variant="caption" color="text.secondary">
        Employee financials are attributed to the booking employee in LEBT. Business totals also include orders without
        an employee mapping. Tier progress uses qualifying completed travel in the current membership year, rather than
        lifetime orders. Silver grants are confirmed successful LEBT upgrades from the business activity log; the date
        is the latest recorded Silver grant (Sydney time).
      </Typography>
    </Box>
  );
}
